import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  BarChart as BarChartIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { BarChart } from "@mui/x-charts/BarChart";
import { PieChart } from "@mui/x-charts/PieChart";
import { RepricerArticlesApi } from "../../../services/api/multi/repricer";
import {
  fmtStatsMoney,
  fmtStatsPct,
  fmtStatsQty,
  OTTO_AD_DATA_MAX_DAYS,
  resolveSalesStatsDays,
  resolveSalesStatsWindow,
  SALES_STATS_ALL_DAYS,
  SALES_STATS_ALL_PERIOD,
  SALES_STATS_CUSTOM_PERIOD,
  SALES_STATS_DAY_OPTIONS,
  SALES_STATS_MAX_DAYS,
} from "../hooks/useArticleSalesStats";

const CHANNEL_LABELS = {
  otto: "Otto",
  kaufland: "Kaufland",
  shopify: "Shopify",
  fbm: "FBM",
  fba: "FBA",
  other: "Other",
  all: "All channels",
};

const CHANNEL_COLORS = {
  otto: "#f00020",
  kaufland: "#e10915",
  shopify: "#96bf48",
  fbm: "#1976d2",
  fba: "#ff9900",
  other: "#9e9e9e",
};

function KpiCard({ label, value, hint, color }) {
  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 1,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper",
        height: "100%",
      }}
    >
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography
        variant="h6"
        fontWeight={600}
        sx={{ color: color || "text.primary", mt: 0.25 }}
      >
        {value}
      </Typography>
      {hint ? (
        <Typography variant="caption" color="text.secondary">
          {hint}
        </Typography>
      ) : null}
    </Box>
  );
}

/**
 * Full-screen-ish dialog with KPI cards + charts for one article's sales/profit.
 */
export function ArticleSalesStatsDialog({
  open,
  onClose,
  sku,
  articlename,
  channel = "all",
  defaultDays = 7,
  defaultFromDate = null,
  defaultToDate = null,
}) {
  const [periodSelect, setPeriodSelect] = useState(defaultDays);
  const [customDaysInput, setCustomDaysInput] = useState("90");
  const [customFrom, setCustomFrom] = useState(defaultFromDate || "");
  const [customTo, setCustomTo] = useState(defaultToDate || "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);

  const statsWindow = useMemo(
    () =>
      resolveSalesStatsWindow({
        periodSelect,
        customDaysInput,
        customFrom,
        customTo,
      }),
    [periodSelect, customDaysInput, customFrom, customTo],
  );

  const days = useMemo(() => {
    if (statsWindow.from_date && statsWindow.to_date) {
      const start = new Date(`${statsWindow.from_date}T00:00:00`);
      const end = new Date(`${statsWindow.to_date}T00:00:00`);
      return Math.max(1, Math.round((end - start) / (24 * 60 * 60 * 1000)) + 1);
    }
    return resolveSalesStatsDays(periodSelect, customDaysInput);
  }, [statsWindow, periodSelect, customDaysInput]);

  const showOttoAdRangeWarning =
    (days === SALES_STATS_ALL_DAYS || days > OTTO_AD_DATA_MAX_DAYS) &&
    (channel === "otto" || channel === "all");

  useEffect(() => {
    if (!open) return;
    if (defaultFromDate && defaultToDate) {
      setPeriodSelect(SALES_STATS_CUSTOM_PERIOD);
      setCustomFrom(defaultFromDate);
      setCustomTo(defaultToDate);
    } else if (
      defaultDays === SALES_STATS_ALL_DAYS ||
      defaultDays === SALES_STATS_ALL_PERIOD
    ) {
      setPeriodSelect(SALES_STATS_ALL_PERIOD);
    } else if (SALES_STATS_DAY_OPTIONS.includes(defaultDays)) {
      setPeriodSelect(defaultDays);
    } else {
      setPeriodSelect(SALES_STATS_CUSTOM_PERIOD);
      setCustomDaysInput(String(defaultDays || 90));
    }
  }, [open, sku, defaultDays, defaultFromDate, defaultToDate]);

  useEffect(() => {
    if (!open || !sku) return undefined;
    let cancelled = false;

    (async () => {
      setLoading(true);
      setError(null);
      setStats(null);
      try {
        const res = await RepricerArticlesApi.getArticleSalesStats(sku, {
          channel,
          ...statsWindow,
        });
        if (!cancelled) setStats(res.data || null);
      } catch (e) {
        if (!cancelled) {
          setStats(null);
          setError(
            e.response?.data?.detail || e.message || "Failed to load stats",
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, sku, channel, statsWindow]);

  const byChannelEntries = useMemo(() => {
    const raw = stats?.by_channel || {};
    return Object.entries(raw)
      .map(([ch, chStats]) => ({
        channel: ch,
        label: CHANNEL_LABELS[ch] || ch,
        color: CHANNEL_COLORS[ch] || "#607d8b",
        sales_qty: Number(chStats?.sales_qty || 0),
        period_profit: Number(chStats?.period_profit || 0),
        gross_revenue: Number(chStats?.gross_revenue || 0),
        retour_qty: Number(chStats?.retour_qty || 0),
      }))
      .filter(
        (row) =>
          row.sales_qty > 0 || row.retour_qty > 0 || row.period_profit !== 0,
      )
      .sort((a, b) => b.sales_qty - a.sales_qty);
  }, [stats]);

  const showByChannel = channel === "all" && byChannelEntries.length > 0;

  const salesVsRetourSeries = useMemo(() => {
    if (!stats) return null;
    return {
      labels: ["Sales", "Returns"],
      qty: [Number(stats.sales_qty || 0), Number(stats.retour_qty || 0)],
      money: [
        Number(stats.gross_revenue || 0),
        Number(stats.retour_gross || 0),
      ],
    };
  }, [stats]);

  const profitSignColor =
    stats?.period_profit == null
      ? undefined
      : Number(stats.period_profit) >= 0
        ? "success.main"
        : "error.main";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        <Stack spacing={0.25}>
          <Typography variant="h6" component="span">
            Sales statistics
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {sku}
            {articlename ? ` — ${articlename}` : ""}
            {channel && channel !== "all"
              ? ` · ${CHANNEL_LABELS[channel] || channel}`
              : ""}
          </Typography>
          {stats?.is_one_to_one_bundle &&
            (stats.one_to_one_linked_skus || []).length > 0 && (
              <Stack
                direction="row"
                spacing={0.5}
                flexWrap="wrap"
                useFlexGap
                sx={{ mt: 0.75 }}
              >
                {(stats.one_to_one_linked_skus || []).map((linkedSku) => (
                  <Chip
                    key={linkedSku}
                    size="small"
                    label={`1:1 ${linkedSku}`}
                    variant="outlined"
                  />
                ))}
              </Stack>
            )}
        </Stack>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
          sx={{ mb: 2 }}
        >
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel id="article-sales-stats-days">Period</InputLabel>
            <Select
              labelId="article-sales-stats-days"
              label="Period"
              value={
                periodSelect === SALES_STATS_CUSTOM_PERIOD ||
                periodSelect === SALES_STATS_ALL_PERIOD
                  ? periodSelect
                  : periodSelect
              }
              onChange={(e) => {
                const v = e.target.value;
                if (
                  v === SALES_STATS_CUSTOM_PERIOD ||
                  v === SALES_STATS_ALL_PERIOD
                ) {
                  setPeriodSelect(v);
                } else {
                  setPeriodSelect(Number(v));
                }
              }}
            >
              {SALES_STATS_DAY_OPTIONS.map((d) => (
                <MenuItem key={d} value={d}>
                  Last {d} days
                </MenuItem>
              ))}
              <MenuItem value={SALES_STATS_ALL_PERIOD}>All time</MenuItem>
              <MenuItem value={SALES_STATS_CUSTOM_PERIOD}>Custom…</MenuItem>
            </Select>
          </FormControl>
          {periodSelect === SALES_STATS_CUSTOM_PERIOD && (
            <>
              <TextField
                size="small"
                type="date"
                label="From"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 160 }}
              />
              <TextField
                size="small"
                type="date"
                label="To"
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 160 }}
              />
              {(!customFrom || !customTo) && (
                <TextField
                  size="small"
                  type="number"
                  label="Last N days"
                  value={customDaysInput}
                  onChange={(e) => setCustomDaysInput(e.target.value)}
                  inputProps={{ min: 1, max: SALES_STATS_MAX_DAYS }}
                  sx={{ width: 120 }}
                />
              )}
            </>
          )}
        </Stack>

        {showOttoAdRangeWarning && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Otto SPA ad data is only available for the last{" "}
            {OTTO_AD_DATA_MAX_DAYS} days. Sales and profit for{" "}
            {days === SALES_STATS_ALL_DAYS
              ? "all time"
              : `the full ${days}-day window`}{" "}
            are shown, but ad KPIs and ad cost attribution may not cover the
            entire period.
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : !stats ? (
          <Typography color="text.secondary">No data available.</Typography>
        ) : (
          <Stack spacing={3} sx={{ pb: 1 }}>
            <Grid container spacing={1.5}>
              <Grid item xs={6} sm={4} md={3}>
                <KpiCard
                  label="Sales qty"
                  value={fmtStatsQty(stats.sales_qty)}
                  hint={`${fmtStatsQty(stats.retour_qty)} returns`}
                />
              </Grid>
              <Grid item xs={6} sm={4} md={3}>
                <KpiCard
                  label="Return rate"
                  value={fmtStatsPct(stats.retour_rate)}
                />
              </Grid>
              <Grid item xs={6} sm={4} md={3}>
                <KpiCard
                  label="Period profit"
                  value={fmtStatsMoney(stats.period_profit)}
                  color={profitSignColor}
                  hint={[
                    `Ø ${fmtStatsMoney(stats.avg_profit_per_sale)} / sale`,
                    Number(stats.return_cost_total) > 0
                      ? `returns −${fmtStatsMoney(stats.return_cost_total)}`
                      : null,
                    Number(stats.ad_cost_applied) > 0
                      ? `ads −${fmtStatsMoney(stats.ad_cost_applied)}`
                      : null,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                />
              </Grid>
              <Grid item xs={6} sm={4} md={3}>
                <KpiCard
                  label="Gross revenue"
                  value={fmtStatsMoney(stats.gross_revenue)}
                  hint={`Return gross ${fmtStatsMoney(stats.retour_gross)}`}
                />
              </Grid>
              <Grid item xs={6} sm={4} md={3}>
                <KpiCard
                  label="Total return costs"
                  value={fmtStatsMoney(stats.return_cost_total)}
                  hint={
                    Number(stats.retour_qty) > 0
                      ? "Σ refund_expenses (Plenty pkg+extras / Amazon Finances)"
                      : "refund_expenses from return imports"
                  }
                />
              </Grid>
              <Grid item xs={6} sm={4} md={3}>
                <KpiCard
                  label="Avg sell price"
                  value={fmtStatsMoney(stats.avg_sell_price)}
                />
              </Grid>
              <Grid item xs={6} sm={4} md={3}>
                <KpiCard
                  label="Avg buy price"
                  value={fmtStatsMoney(stats.avg_buy_price)}
                />
              </Grid>
              {(channel === "otto" || channel === "all") && (
                <>
                  <Grid item xs={6} sm={4} md={3}>
                    <KpiCard
                      label="Total ad costs"
                      value={fmtStatsMoney(stats.total_ad_costs)}
                      hint="Otto SPA (last 90d)"
                    />
                  </Grid>
                  <Grid item xs={6} sm={4} md={3}>
                    <KpiCard
                      label="Total ad sales"
                      value={fmtStatsQty(stats.total_ad_sales)}
                      hint="Ad-attributed units (Otto SPA, last 90d)"
                    />
                  </Grid>
                  <Grid item xs={6} sm={4} md={3}>
                    <KpiCard
                      label="Adspend / sale"
                      value={
                        stats.ad_spend_per_sale != null
                          ? fmtStatsMoney(stats.ad_spend_per_sale)
                          : Number(stats.total_ad_costs) > 0 &&
                              Number(stats.total_ad_sales) > 0
                            ? fmtStatsMoney(
                                Number(stats.total_ad_costs) /
                                  Number(stats.total_ad_sales),
                              )
                            : "—"
                      }
                      hint={
                        Number(stats.total_ad_sales) > 0
                          ? "total_ad_costs ÷ total_ad_sales"
                          : "No ad sales (division skipped)"
                      }
                    />
                  </Grid>
                  {Number(stats.ad_cost_applied) > 0 && (
                    <Grid item xs={6} sm={4} md={3}>
                      <KpiCard
                        label="Ad cost applied"
                        value={fmtStatsMoney(stats.ad_cost_applied)}
                        hint="Deducted from profit only"
                      />
                    </Grid>
                  )}
                </>
              )}
            </Grid>

            <Divider />

            {salesVsRetourSeries && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Sales vs returns ({days}d)
                </Typography>
                {salesVsRetourSeries.qty[0] === 0 &&
                salesVsRetourSeries.qty[1] === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No sales or returns in this period.
                  </Typography>
                ) : (
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <BarChart
                        height={220}
                        xAxis={[
                          {
                            data: salesVsRetourSeries.labels,
                            scaleType: "band",
                          },
                        ]}
                        series={[
                          {
                            data: salesVsRetourSeries.qty,
                            label: "Quantity",
                            color: "#1976d2",
                            valueFormatter: (v) => `${v ?? 0} units`,
                          },
                        ]}
                        margin={{ left: 40, right: 20, top: 20, bottom: 30 }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <BarChart
                        height={220}
                        xAxis={[
                          {
                            data: salesVsRetourSeries.labels,
                            scaleType: "band",
                          },
                        ]}
                        series={[
                          {
                            data: salesVsRetourSeries.money,
                            label: "Gross €",
                            color: "#2e7d32",
                            valueFormatter: (v) => fmtStatsMoney(v),
                          },
                        ]}
                        margin={{ left: 50, right: 20, top: 20, bottom: 30 }}
                      />
                    </Grid>
                  </Grid>
                )}
              </Box>
            )}

            {showByChannel && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  By channel
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">
                      Sales quantity
                    </Typography>
                    <PieChart
                      height={240}
                      series={[
                        {
                          data: byChannelEntries.map((row, i) => ({
                            id: i,
                            value: Math.max(row.sales_qty, 0),
                            label: `${row.label} (${fmtStatsQty(row.sales_qty)})`,
                            color: row.color,
                          })),
                          innerRadius: 40,
                          paddingAngle: 2,
                          cornerRadius: 3,
                        },
                      ]}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">
                      Returns quantity
                    </Typography>
                    {byChannelEntries.every((r) => r.retour_qty <= 0) ? (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ py: 6, textAlign: "center" }}
                      >
                        No returns in this period.
                      </Typography>
                    ) : (
                      <PieChart
                        height={240}
                        series={[
                          {
                            data: byChannelEntries
                              .filter((row) => row.retour_qty > 0)
                              .map((row, i) => ({
                                id: i,
                                value: Math.max(row.retour_qty, 0),
                                label: `${row.label} (${fmtStatsQty(row.retour_qty)})`,
                                color: row.color,
                              })),
                            innerRadius: 40,
                            paddingAngle: 2,
                            cornerRadius: 3,
                          },
                        ]}
                      />
                    )}
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">
                      Period profit (€)
                    </Typography>
                    <BarChart
                      height={240}
                      layout="horizontal"
                      yAxis={[
                        {
                          data: byChannelEntries.map((r) => r.label),
                          scaleType: "band",
                          width: 80,
                        },
                      ]}
                      series={[
                        {
                          data: byChannelEntries.map((r) => r.period_profit),
                          label: "Profit",
                          color: "#6a1b9a",
                          valueFormatter: (v) => fmtStatsMoney(v),
                        },
                      ]}
                      margin={{ left: 10, right: 20, top: 10, bottom: 10 }}
                    />
                  </Grid>
                </Grid>
                <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 1 }}>
                  {byChannelEntries.map((row) => (
                    <Chip
                      key={row.channel}
                      size="small"
                      variant="outlined"
                      sx={{ borderColor: row.color }}
                      label={`${row.label}: ${fmtStatsQty(row.sales_qty)} sold · ${fmtStatsQty(row.retour_qty)} returns · ${fmtStatsMoney(row.period_profit)}`}
                    />
                  ))}
                </Stack>
              </Box>
            )}
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** Action-row button that opens sales stats for a SKU. */
export function SalesStatsOpenButton({ onClick, disabled, size = "small" }) {
  return (
    <Tooltip title="Sales & profit statistics">
      <span>
        <IconButton
          size={size}
          color="primary"
          onClick={(e) => {
            e.stopPropagation();
            onClick?.(e);
          }}
          disabled={disabled}
        >
          <BarChartIcon fontSize="small" />
        </IconButton>
      </span>
    </Tooltip>
  );
}
